/**
 * Performance Dashboard Component
 * 
 * Visual dashboard for monitoring application performance in real-time.
 * Provides insights into route transitions, API calls, component renders,
 * and Web Vitals metrics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChartBarIcon, 
  ClockIcon, 
  CpuChipIcon, 
  ArrowPathIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ServerIcon,
  GlobeAltIcon,
  CubeIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { performanceMonitor } from '../../services/performanceMonitor';
import { usePerformanceData } from '../../hooks/usePerformanceMonitor';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { apiService } from '../../services/api';
import { toast } from 'react-toastify';

interface PerformanceDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ isOpen, onClose }) => {
  const [summary, setSummary] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [isMonitoringEnabled, setIsMonitoringEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'routes' | 'api' | 'components' | 'vitals' | 'insights'>('overview');
  const { getSummary, generateReport, exportData, clear } = usePerformanceData();
  
  // AI Insights state
  const [insightsConfig, setInsightsConfig] = useState({
    includeRoutes: true,
    includeAPIs: true,
    includeComponents: true,
    includeVitals: true,
    includeMemory: true,
    customComment: ''
  });
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [analysisTaskId, setAnalysisTaskId] = useState<string | null>(null);
  
  // Auth context
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  
  // Permission check - only super admins can generate insights
  const canGenerateInsights = user?.role === 'super_admin';

  const refreshData = useCallback(() => {
    setSummary(getSummary());
    setReport(generateReport());
  }, [getSummary, generateReport]);

  useEffect(() => {
    if (isOpen) {
      refreshData();
    }
  }, [isOpen, refreshData]);

  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(refreshData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, refreshData]);

  const handleToggleMonitoring = () => {
    const newState = !isMonitoringEnabled;
    setIsMonitoringEnabled(newState);
    performanceMonitor.setEnabled(newState);
  };

  const handleExport = () => {
    exportData();
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all performance data?')) {
      clear();
      refreshData();
    }
  };

  const getWebVitalRating = (metric: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
    const thresholds: Record<string, [number, number]> = {
      'CLS': [0.1, 0.25],
      'FID': [100, 300],
      'FCP': [1800, 3000],
      'LCP': [2500, 4000],
      'TTFB': [800, 1800]
    };

    const [good, poor] = thresholds[metric] || [0, 0];
    if (value <= good) return 'good';
    if (value <= poor) return 'needs-improvement';
    return 'poor';
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // AI Insights functions
  const generatePerformanceAnalysis = async () => {
    if (!canGenerateInsights || !currentOrganization) {
      toast.error('You do not have permission to generate performance analysis');
      return;
    }

    try {
      setIsGeneratingAnalysis(true);
      
      // Prepare performance data for analysis
      const performanceData = {
        summary,
        report,
        config: insightsConfig,
        timestamp: Date.now()
      };
      
      const response = await apiService.api.post('/api/tasks/performance-analysis', {
        performanceData,
        config: insightsConfig,
        organizationId: currentOrganization.id,
        userId: user?.id
      });
      
      const taskId = response.data.task_id;
      setAnalysisTaskId(taskId);
      
      // Start polling for results
      pollTaskResult(taskId);
      
      toast.success('Performance analysis started successfully');
    } catch (error) {
      console.error('Error generating performance analysis:', error);
      toast.error('Failed to start performance analysis');
      setIsGeneratingAnalysis(false);
    }
  };

  const pollTaskResult = async (taskId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiService.api.get(`/api/tasks/${taskId}/result`);
        const { status, result, error } = response.data;
        
        if (status === 'completed') {
          clearInterval(pollInterval);
          setAnalysisResult(result);
          setIsGeneratingAnalysis(false);
          toast.success('Performance analysis completed!');
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          setIsGeneratingAnalysis(false);
          toast.error(`Analysis failed: ${error}`);
        }
      } catch (error) {
        console.error('Error polling task result:', error);
        clearInterval(pollInterval);
        setIsGeneratingAnalysis(false);
        toast.error('Failed to get analysis results');
      }
    }, 2000);
    
    // Clear interval after 5 minutes to prevent infinite polling
    setTimeout(() => {
      clearInterval(pollInterval);
      if (isGeneratingAnalysis) {
        setIsGeneratingAnalysis(false);
        toast.warning('Analysis is taking longer than expected');
      }
    }, 300000);
  };

  const copyAnalysisToClipboard = () => {
    if (analysisResult) {
      navigator.clipboard.writeText(JSON.stringify(analysisResult, null, 2));
      toast.success('Analysis copied to clipboard');
    }
  };

  const exportAnalysisReport = () => {
    if (analysisResult) {
      const dataStr = JSON.stringify(analysisResult, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `performance-analysis-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Analysis report exported');
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'routes', label: 'Routes', icon: GlobeAltIcon },
    { id: 'api', label: 'API Calls', icon: ServerIcon },
    { id: 'components', label: 'Components', icon: CubeIcon },
    { id: 'vitals', label: 'Web Vitals', icon: ArrowTrendingUpIcon },
    ...(canGenerateInsights ? [{ id: 'insights', label: 'AI Insights', icon: SparklesIcon }] : []),
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn p-4">
      <div className="bg-white rounded-xl w-full h-full max-w-7xl max-h-[90vh] flex flex-col shadow-xl animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChartBarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-sm font-bold text-gray-900">Performance Dashboard</h2>
            <div className={clsx(
              "flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all",
              isMonitoringEnabled 
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            )}>
              <div className={clsx(
                "w-2 h-2 rounded-full",
                isMonitoringEnabled ? "bg-green-500 animate-pulse" : "bg-red-500"
              )} />
              {isMonitoringEnabled ? 'Monitoring Active' : 'Monitoring Paused'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleMonitoring}
              className={clsx(
                "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                isMonitoringEnabled 
                  ? "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  : "bg-green-600 text-white hover:bg-green-700 shadow-sm"
              )}
            >
              {isMonitoringEnabled ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              {isMonitoringEnabled ? 'Pause' : 'Resume'}
            </button>
            
            <select
              value={refreshInterval || ''}
              onChange={(e) => setRefreshInterval(e.target.value ? parseInt(e.target.value) : null)}
              className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Manual Refresh</option>
              <option value="1000">1s Auto-refresh</option>
              <option value="5000">5s Auto-refresh</option>
              <option value="10000">10s Auto-refresh</option>
            </select>

            <button
              onClick={refreshData}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh data"
            >
              <ArrowPathIcon className="w-5 h-5 text-gray-600" />
            </button>

            <div className="h-6 w-px bg-gray-300 mx-1" />

            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
              title="Export performance data"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              Export
            </button>

            <button
              onClick={handleClear}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600"
              title="Clear all data"
            >
              <TrashIcon className="w-5 h-5" />
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={clsx(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all",
                  activeTab === tab.id
                    ? "bg-white text-primary-700 shadow-sm border border-gray-200"
                    : "text-gray-600 hover:bg-white hover:text-gray-900 hover:shadow-sm"
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
          {!summary ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <ArrowPathIcon className="w-12 h-12 text-gray-400 animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Loading performance data...</p>
              </div>
            </div>
          ) : (
            <div>
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">Route Transitions</span>
                        <GlobeAltIcon className="w-5 h-5 text-blue-500" />
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {summary.overview.totalRouteTransitions}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Avg: {formatDuration(summary.overview.averageRouteTime || 0)}
                      </div>
                      {summary.slowRoutes.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-orange-600">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {summary.slowRoutes.length} slow routes
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">API Calls</span>
                        <ServerIcon className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {summary.overview.totalAPIcalls}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Avg: {formatDuration(summary.overview.averageAPITime || 0)}
                      </div>
                      {summary.slowAPIs.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-orange-600">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {summary.slowAPIs.length} slow APIs
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">Memory Usage</span>
                        <CpuChipIcon className="w-5 h-5 text-purple-500" />
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {summary.overview.memoryUsage 
                          ? formatBytes(summary.overview.memoryUsage.usedJSHeapSize)
                          : 'N/A'
                        }
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {summary.overview.memoryUsage 
                          ? `of ${formatBytes(summary.overview.memoryUsage.totalJSHeapSize)}`
                          : 'Not available'
                        }
                      </div>
                    </div>

                    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">Performance Score</span>
                        <ArrowTrendingUpIcon className="w-5 h-5 text-orange-500" />
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {Object.keys(summary.webVitals).length > 0 
                          ? Math.round((Object.values(summary.webVitals).filter((v: any) => getWebVitalRating(v.name, v) === 'good').length / Object.keys(summary.webVitals).length) * 100) + '%'
                          : 'Calculating...'
                        }
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {Object.keys(summary.webVitals).length} metrics tracked
                      </div>
                    </div>
                  </div>

                  {/* Performance Insights */}
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
                      Performance Insights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {summary.slowRoutes.length === 0 && summary.slowAPIs.length === 0 && summary.slowComponents.length === 0 ? (
                        <div className="col-span-full flex items-center justify-center py-8 text-green-600">
                          <CheckCircleIcon className="w-6 h-6 mr-2" />
                          <span className="font-medium">All performance metrics are within acceptable ranges!</span>
                        </div>
                      ) : (
                        <>
                          {summary.slowRoutes.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">Slow Routes (&gt;2s)</h4>
                              {summary.slowRoutes.slice(0, 3).map((route: any, idx: number) => (
                                <div key={idx} className="text-sm bg-red-50 p-2 rounded border border-red-200">
                                  <div className="font-medium text-red-900">{route.from} → {route.to}</div>
                                  <div className="text-red-700">{formatDuration(route.duration)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {summary.slowAPIs.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">Slow API Calls (&gt;2s)</h4>
                              {summary.slowAPIs.slice(0, 3).map((api: any, idx: number) => (
                                <div key={idx} className="text-sm bg-yellow-50 p-2 rounded border border-yellow-200">
                                  <div className="font-medium text-yellow-900 truncate">{api.method} {api.url}</div>
                                  <div className="text-yellow-700">{formatDuration(api.duration)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {summary.slowComponents.length > 0 && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">Slow Components (&gt;100ms)</h4>
                              {summary.slowComponents.slice(0, 3).map((comp: any, idx: number) => (
                                <div key={idx} className="text-sm bg-orange-50 p-2 rounded border border-orange-200">
                                  <div className="font-medium text-orange-900">{comp.componentName}</div>
                                  <div className="text-orange-700">{formatDuration(comp.renderTime)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'routes' && (
                <div className="bg-white rounded-lg shadow border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <GlobeAltIcon className="w-5 h-5 text-blue-500" />
                      Route Transition Performance
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From → To</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Calls</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(report?.routeTransitions || []).map((route: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                              {route.from} → {route.to}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {formatDuration(route.duration || 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {route.apiCalls.length}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {route.duration > 2000 ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                                  Slow
                                </span>
                              ) : route.duration > 1000 ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                                  Moderate
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                  Good
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'api' && (
                <div className="bg-white rounded-lg shadow border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <ServerIcon className="w-5 h-5 text-green-500" />
                      API Call Performance
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(report?.apiCalls || []).slice(0, 50).map((api: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={clsx(
                                "inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border",
                                api.method === 'GET' && "bg-blue-50 text-blue-700 border-blue-200",
                                api.method === 'POST' && "bg-green-50 text-green-700 border-green-200",
                                api.method === 'PUT' && "bg-yellow-50 text-yellow-700 border-yellow-200",
                                api.method === 'DELETE' && "bg-red-50 text-red-700 border-red-200"
                              )}>
                                {api.method}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-900 max-w-xs truncate" title={api.url}>
                              {api.url}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {formatDuration(api.duration)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={clsx(
                                "inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border",
                                api.status >= 200 && api.status < 300 && "bg-green-50 text-green-700 border-green-200",
                                api.status >= 300 && api.status < 400 && "bg-yellow-50 text-yellow-700 border-yellow-200",
                                api.status >= 400 && "bg-red-50 text-red-700 border-red-200"
                              )}>
                                {api.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {api.size ? formatBytes(api.size) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'components' && (
                <div className="bg-white rounded-lg shadow border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <CubeIcon className="w-5 h-5 text-purple-500" />
                      Component Render Performance
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Component</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Render Time</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Props Count</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(report?.componentRenders || []).slice(0, 50).map((comp: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                              {comp.componentName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {formatDuration(comp.renderTime)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {comp.props ? Object.keys(comp.props).length : 0}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                              {new Date(comp.timestamp).toLocaleTimeString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'vitals' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <ArrowTrendingUpIcon className="w-5 h-5 text-orange-500" />
                      Core Web Vitals
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(report?.webVitals || {}).map(([metric, value]: [string, any]) => {
                        const rating = getWebVitalRating(metric, value);
                        const descriptions: Record<string, string> = {
                          'CLS': 'Cumulative Layout Shift',
                          'FID': 'First Input Delay',
                          'FCP': 'First Contentful Paint',
                          'LCP': 'Largest Contentful Paint',
                          'TTFB': 'Time to First Byte'
                        };
                        
                        return (
                          <div key={metric} className="bg-white rounded-lg p-4 border border-gray-200 shadow hover:shadow-lg transition-shadow">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-gray-900 text-xs">{metric}</h4>
                                <p className="text-xs text-gray-500 mt-0.5">{descriptions[metric]}</p>
                              </div>
                              <div className={clsx(
                                "px-2 py-1 rounded-lg text-xs font-medium border",
                                rating === 'good' && "bg-green-50 text-green-700 border-green-200",
                                rating === 'needs-improvement' && "bg-yellow-50 text-yellow-700 border-yellow-200",
                                rating === 'poor' && "bg-red-50 text-red-700 border-red-200"
                              )}>
                                {rating.replace('-', ' ')}
                              </div>
                            </div>
                            <div className="text-lg font-bold text-gray-900 mt-1">
                              {formatDuration(value)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2 text-sm">
                      <ExclamationTriangleIcon className="w-5 h-5 text-blue-600" />
                      Performance Optimization Tips
                    </h4>
                    <ul className="text-xs text-blue-800 space-y-2">
                      <li>• Route transitions &gt;2s indicate heavy initial loads - consider code splitting</li>
                      <li>• API calls &gt;2s should be optimized with caching or pagination</li>
                      <li>• Component renders &gt;100ms may benefit from React.memo or useMemo</li>
                      <li>• Monitor CLS scores to ensure visual stability during loading</li>
                      <li>• Use Chrome DevTools Performance tab for detailed profiling</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'insights' && canGenerateInsights && (
                <div className="space-y-6">
                  {/* Step 1: Configure Analysis */}
                  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <SparklesIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900">Configure Performance Analysis</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-gray-700">Analysis Scope</h4>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={insightsConfig.includeRoutes}
                              onChange={(e) => setInsightsConfig(prev => ({ ...prev, includeRoutes: e.target.checked }))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Route Performance</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={insightsConfig.includeAPIs}
                              onChange={(e) => setInsightsConfig(prev => ({ ...prev, includeAPIs: e.target.checked }))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">API Performance</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={insightsConfig.includeComponents}
                              onChange={(e) => setInsightsConfig(prev => ({ ...prev, includeComponents: e.target.checked }))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Component Renders</span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-gray-700">Additional Metrics</h4>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={insightsConfig.includeVitals}
                              onChange={(e) => setInsightsConfig(prev => ({ ...prev, includeVitals: e.target.checked }))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Web Vitals</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={insightsConfig.includeMemory}
                              onChange={(e) => setInsightsConfig(prev => ({ ...prev, includeMemory: e.target.checked }))}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Memory Usage</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Context (Optional)
                      </label>
                      <textarea
                        value={insightsConfig.customComment}
                        onChange={(e) => setInsightsConfig(prev => ({ ...prev, customComment: e.target.value }))}
                        placeholder="Add any specific context or questions about performance..."
                        className="block w-full rounded-md border-2 border-blue-300 bg-blue-50 px-4 py-3 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:bg-white disabled:bg-gray-50 disabled:border-gray-300"
                        rows={3}
                      />
                    </div>
                    
                    <button
                      onClick={generatePerformanceAnalysis}
                      disabled={isGeneratingAnalysis || !summary || !report}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-xs"
                    >
                      {isGeneratingAnalysis ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          Generating Analysis...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-4 h-4" />
                          Generate Performance Analysis
                        </>
                      )}
                    </button>
                  </div>

                  {/* Step 2: Generated Analysis */}
                  {analysisResult && (
                    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          Performance Analysis Results
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={copyAnalysisToClipboard}
                            className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-xs"
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                            Copy
                          </button>
                          <button
                            onClick={exportAnalysisReport}
                            className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-xs"
                          >
                            <ShareIcon className="w-4 h-4" />
                            Export
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Executive Summary */}
                        {analysisResult.summary && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <h4 className="font-semibold text-blue-900 mb-2 text-sm">Executive Summary</h4>
                            <p className="text-xs text-blue-800">{analysisResult.summary}</p>
                          </div>
                        )}
                        
                        {/* Critical Issues */}
                        {analysisResult.criticalIssues && analysisResult.criticalIssues.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <h4 className="font-semibold text-red-900 mb-2 text-sm">Critical Issues</h4>
                            <ul className="text-xs text-red-800 space-y-1">
                              {analysisResult.criticalIssues.map((issue: string, idx: number) => (
                                <li key={idx}>• {issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Recommendations */}
                        {analysisResult.recommendations && analysisResult.recommendations.length > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <h4 className="font-semibold text-green-900 mb-2 text-sm">Recommendations</h4>
                            <ol className="text-xs text-green-800 space-y-1">
                              {analysisResult.recommendations.map((rec: string, idx: number) => (
                                <li key={idx}>{idx + 1}. {rec}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        
                        {/* Full Analysis */}
                        {analysisResult.fullAnalysis && (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <h4 className="font-semibold text-gray-900 mb-2 text-sm">Detailed Analysis</h4>
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                              {analysisResult.fullAnalysis}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Task Status */}
                  {analysisTaskId && (
                    <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Analysis Task Status</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Task ID:</span>
                          <span className="font-mono text-gray-900">{analysisTaskId}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <span className={clsx(
                            "px-2 py-1 rounded text-xs font-medium",
                            isGeneratingAnalysis 
                              ? "bg-yellow-100 text-yellow-800" 
                              : analysisResult 
                                ? "bg-green-100 text-green-800" 
                                : "bg-gray-100 text-gray-800"
                          )}>
                            {isGeneratingAnalysis ? 'In Progress' : analysisResult ? 'Completed' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* Add these animations to your global CSS or Tailwind config */
const animationStyles = `
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-slideUp {
  animation: slideUp 0.3s ease-out;
}
`;